import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ContentWrapper } from '../../shared/ContentWrapper/ContentWrapper';
import { Title, Form } from './Schedules.styled';
import { ScheduleForm } from '../../components/Schedules/ScheduleForm';

import { addEditScheduleDay, getSchedules } from '../../api/schedules';
import { getToken } from '../../services/auth';

import { SignUpButton } from '../../components/SignUpForms/SignUpForms.styled';

// Popup al guardar (MUI v4)
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
const Alert = (props) => <MuiAlert elevation={6} variant="filled" {...props} />;

const Schedules = () => {
  const { t } = useTranslation();
  const storeId = useSelector((state) => state.store.id);

  const [schedules, setSchedules] = useState(null);

  // Estado que viene del ScheduleForm (controlado)
  const [draftSchedules, setDraftSchedules] = useState(null);

  // validateAll expuesto por ScheduleForm
  const [validateFn, setValidateFn] = useState(null);

  // Snackbar de guardado OK/ERROR
  const [snack, setSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const response = await getSchedules({ token: getToken(), id: storeId });
        const schedulesByDay = response.results.reduce((acc, schedule) => {
          acc[schedule.day.toLowerCase()] = schedule;
          return acc;
        }, {});
        setSchedules(schedulesByDay);
        setDraftSchedules(null);
      } catch (error) {
        console.error(error);
      }
    };

    if (storeId) fetchSchedules();
  }, [storeId]);

  // MEMOIZADO => evita freeze
  const handleExposeValidate = useCallback((fn) => {
    setValidateFn(() => fn);
  }, []);

  const days = [
    { day: 0, name: 'monday' },
    { day: 1, name: 'tuesday' },
    { day: 2, name: 'wednesday' },
    { day: 3, name: 'thursday' },
    { day: 4, name: 'friday' },
    { day: 5, name: 'saturday' },
    { day: 6, name: 'sunday' },
  ];

  const onSubmit = async (event) => {
    event.preventDefault();

    // 1) Validar antes de guardar (marca rojo y popup en ScheduleForm)
    if (typeof validateFn === 'function') {
      const ok = validateFn();
      if (!ok) return;
    }

    const source = draftSchedules; // preferido

    try {
      for (const dayInfo of days) {
        let fromHour, fromMinutes, toHour, toMinutes, close;

        if (source && source[dayInfo.name]) {
          fromHour = source[dayInfo.name].fromHour;
          fromMinutes = source[dayInfo.name].fromMinutes;
          toHour = source[dayInfo.name].toHour;
          toMinutes = source[dayInfo.name].toMinutes;
          close = source[dayInfo.name].close;
        } else {
          // fallback: como tu versión original
          fromHour = event.target[`${dayInfo.name}FromHour`].value;
          fromMinutes = event.target[`${dayInfo.name}FromMinutes`].value;
          toHour = event.target[`${dayInfo.name}ToHour`].value;
          toMinutes = event.target[`${dayInfo.name}ToMinutes`].value;
          close = event.target[`${dayInfo.name}Closed`].checked;
        }

        if (close || (fromHour && fromMinutes && toHour && toMinutes)) {
          await addEditScheduleDay({
            token: getToken(),
            id: storeId,
            params: {
              branch_store_id: storeId,
              day: dayInfo.day,
              fromTime: `${fromHour}:${fromMinutes}:00`,
              toTime: `${toHour}:${toMinutes}:00`,
              close,
            },
          });
        }
      }

      const allClosed =
        source &&
        Object.values(source).length > 0 &&
        Object.values(source).every((s) => s && s.close === true);

      setSnack({
        open: true,
        severity: 'success',
        message: allClosed
          ? 'Saved: the store is CLOSED.'
          : 'Saved: Hours updated correctly.',
      });
    } catch (err) {
      if (err && err.response && err.response.status === 400) {
        setSnack({
          open: true,
          severity: 'error',
          message: 'Please check the hours and try again.',
        });
        return;
      }

      setSnack({
        open: true,
        severity: 'error',
        message: 'Failed to save. Please try again.',
      });
    }
  };

  return (
    <ContentWrapper>
      <Title>{t('Schedules.title')}</Title>

      <Form onSubmit={onSubmit}>
        <ScheduleForm
          schedules={schedules}
          onSchedulesChange={setDraftSchedules}
          exposeValidate={handleExposeValidate}
        />

        {/*Botón Save ORIGINAL abajo (se mantiene) */}
        <SignUpButton type="submit">{t('Schedules.save')}</SignUpButton>
      </Form>

      {/*Snackbar del resultado del guardado */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>
    </ContentWrapper>
  );
};

export default Schedules;
